import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Container,
  Stack,
  Button,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Autocomplete,
  useTheme,
  alpha,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  ReportProblem as ReportProblemIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import UserProfileMenu from '../../components/UserProfileMenu';
import AuthorChip from '../../components/AuthorChip';
import usePermissions from '../../hooks/usePermissions';
import { SiteIncidentService } from '../../services/SiteIncidentService';
import {
  SiteIncident,
  CreateSiteIncidentRequest,
  UpdateSiteIncidentRequest,
  IncidentSeverity,
  IncidentStatus,
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
  SEVERITY_LABEL,
  STATUS_LABEL,
  SERVICE_NAMES,
} from '../../models/SiteIncident';

interface FormState {
  occurred_at: string;
  resolved_at: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  service_name: string;
  title: string;
  summary: string;
  root_cause: string;
  action_taken: string;
  reported_by: string;
}

const EMPTY: FormState = {
  occurred_at: '',
  resolved_at: '',
  severity: 'medium',
  status: 'open',
  service_name: '',
  title: '',
  summary: '',
  root_cause: '',
  action_taken: '',
  reported_by: '',
};

const SEVERITY_COLOR: Record<IncidentSeverity, 'default' | 'success' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'success',
  high: 'warning',
  critical: 'error',
};

const STATUS_COLOR: Record<
  IncidentStatus,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  open: 'error',
  investigating: 'info',
  in_progress: 'warning',
  on_hold: 'default',
  resolved: 'success',
  closed: 'default',
};

const toIso = (local: string): string => {
  if (!local) return '';
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

const toLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromIncident = (i: SiteIncident): FormState => ({
  occurred_at: toLocalInput(i.occurred_at),
  resolved_at: toLocalInput(i.resolved_at),
  severity: i.severity,
  status: i.status,
  service_name: i.service_name ?? '',
  title: i.title ?? '',
  summary: i.summary ?? '',
  root_cause: i.root_cause ?? '',
  action_taken: i.action_taken ?? '',
  reported_by: i.reported_by ?? '',
});

const toRequest = (form: FormState): CreateSiteIncidentRequest => ({
  occurred_at: toIso(form.occurred_at),
  resolved_at: form.resolved_at ? toIso(form.resolved_at) : null,
  severity: form.severity,
  status: form.status,
  service_name: form.service_name.trim() || undefined,
  title: form.title.trim(),
  summary: form.summary || undefined,
  root_cause: form.root_cause || undefined,
  action_taken: form.action_taken || undefined,
  reported_by: form.reported_by || undefined,
});

const formatDt = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
};

interface Snack {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

const SiteIncidentPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useContext(ThemeContext);
  const { canEdit } = usePermissions();
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? Number(projectId) : NaN;

  const stored = localStorage.getItem('selectedProject');
  const projectName = stored ? (JSON.parse(stored).name as string | undefined) : undefined;

  const [incidents, setIncidents] = useState<SiteIncident[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | IncidentStatus>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SiteIncident | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<Snack>({ open: false, message: '', severity: 'success' });

  const refresh = useCallback(async () => {
    if (!Number.isFinite(projectIdNum)) return;
    setLoading(true);
    setError(null);
    try {
      const list = await SiteIncidentService.list(projectIdNum);
      setIncidents(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : '목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [projectIdNum]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (!statusFilter) return incidents;
    return incidents.filter((i) => i.status === statusFilter);
  }, [incidents, statusFilter]);

  const openCount = useMemo(
    () => incidents.filter((i) => i.status === 'open' || i.status === 'investigating').length,
    [incidents],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, occurred_at: toLocalInput(new Date().toISOString()) });
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (i: SiteIncident) => {
    setEditing(i);
    setForm(fromIncident(i));
    setFormError(null);
    setDialogOpen(true);
  };

  const validate = (): string | null => {
    if (!form.title.trim()) return '장애 제목을 입력하세요.';
    if (!form.occurred_at) return '발생일을 입력하세요.';
    const occurredIso = toIso(form.occurred_at);
    if (!occurredIso) return '발생일 형식이 올바르지 않습니다.';
    if (form.resolved_at) {
      const resolvedIso = toIso(form.resolved_at);
      if (!resolvedIso) return '해결일 형식이 올바르지 않습니다.';
      if (new Date(resolvedIso) < new Date(occurredIso)) {
        return '해결일은 발생일 이후여야 합니다.';
      }
    }
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setFormError(v);
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const req: UpdateSiteIncidentRequest = toRequest(form);
        await SiteIncidentService.update(editing.id, req);
        setSnack({ open: true, message: '장애 이력이 수정되었습니다', severity: 'success' });
      } else {
        await SiteIncidentService.create(projectIdNum, toRequest(form));
        setSnack({ open: true, message: '장애 이력이 등록되었습니다', severity: 'success' });
      }
      setDialogOpen(false);
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (i: SiteIncident) => {
    if (!window.confirm(`'${i.title}' 장애 이력을 삭제하시겠습니까?`)) return;
    try {
      await SiteIncidentService.delete(i.id);
      setSnack({ open: true, message: '삭제되었습니다', severity: 'success' });
      await refresh();
    } catch (e) {
      setSnack({ open: true, message: e instanceof Error ? e.message : '삭제 실패', severity: 'error' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/project/${projectId}`)} size="small" aria-label="뒤로가기">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/projects')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 목록
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate(`/project/${projectId}`)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {projectName || `프로젝트 ${projectId}`}
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
              <ReportProblemIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              장애 이력
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <UserProfileMenu />
        </Box>
      </Paper>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              장애 이력 ({incidents.length}건, 진행 중 {openCount}건)
            </Typography>
            <Box sx={{ flex: 1 }} />
            <TextField
              select
              size="small"
              label="상태"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as '' | IncidentStatus)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">전체</MenuItem>
              {INCIDENT_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="새로고침">
              <span>
                <IconButton onClick={refresh} disabled={loading} aria-label="새로고침">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            {canEdit && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                장애 등록
              </Button>
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                {statusFilter ? `'${STATUS_LABEL[statusFilter]}' 상태의 장애가 없습니다.` : '등록된 장애 이력이 없습니다.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>발생일</TableCell>
                    <TableCell>심각도</TableCell>
                    <TableCell>상태</TableCell>
                    <TableCell>서비스</TableCell>
                    <TableCell>제목</TableCell>
                    <TableCell>해결일</TableCell>
                    <TableCell>보고자</TableCell>
                    <TableCell>작성자</TableCell>
                    <TableCell align="right">동작</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((i) => (
                    <TableRow key={i.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDt(i.occurred_at)}</TableCell>
                      <TableCell>
                        <Chip
                          label={SEVERITY_LABEL[i.severity]}
                          color={SEVERITY_COLOR[i.severity]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={STATUS_LABEL[i.status]} color={STATUS_COLOR[i.status]} size="small" />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
                        {i.service_name || '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {i.title}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDt(i.resolved_at)}</TableCell>
                      <TableCell>{i.reported_by || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <AuthorChip name={i.created_by_name} userId={i.created_by_id} label="" dense />
                      </TableCell>
                      <TableCell align="right">
                        {canEdit ? (
                          <>
                            <Tooltip title="수정">
                              <IconButton size="small" onClick={() => openEdit(i)} aria-label="수정">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="삭제">
                              <IconButton size="small" color="error" onClick={() => handleDelete(i)} aria-label="삭제">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '장애 이력 수정' : '장애 이력 등록'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="제목 *"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="심각도 *"
                value={form.severity}
                onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value as IncidentSeverity }))}
                sx={{ flex: 1 }}
              >
                {INCIDENT_SEVERITIES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {SEVERITY_LABEL[s]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="상태 *"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as IncidentStatus }))}
                sx={{ flex: 1 }}
              >
                {INCIDENT_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="발생일 *"
                type="datetime-local"
                value={form.occurred_at}
                onChange={(e) => setForm((f) => ({ ...f, occurred_at: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="해결일"
                type="datetime-local"
                value={form.resolved_at}
                onChange={(e) => setForm((f) => ({ ...f, resolved_at: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Autocomplete
              freeSolo
              options={SERVICE_NAMES as string[]}
              value={form.service_name}
              onChange={(_, v) => setForm((f) => ({ ...f, service_name: typeof v === 'string' ? v : '' }))}
              onInputChange={(_, v) => setForm((f) => ({ ...f, service_name: v }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="영향 받은 서비스"
                  placeholder="목록에서 선택하거나 직접 입력"
                  helperText="목록에 없는 서비스는 직접 입력하세요"
                />
              )}
            />
            <TextField
              label="요약"
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="근본 원인"
              value={form.root_cause}
              onChange={(e) => setForm((f) => ({ ...f, root_cause: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="조치 사항"
              value={form.action_taken}
              onChange={(e) => setForm((f) => ({ ...f, action_taken: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="보고자"
              value={form.reported_by}
              onChange={(e) => setForm((f) => ({ ...f, reported_by: e.target.value }))}
              fullWidth
            />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            취소
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : editing ? '저장' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteIncidentPage;
